/*
 * 宿主桥接。
 *
 * 这是**唯一**知道「当前跑在哪个宿主里」的模块。其他模块只能问它
 * 「能不能做 X」，不能问「是不是在微信里」——feature 里出现 UA 判断视为缺陷。
 *
 * 三种宿主：普通浏览器 / 微信内置浏览器（服务号 H5）/ 小程序 web-view 壳。
 * 能力映射见 apps/web/README.md §能力边界。
 *
 * 桥接只传递能力结果，不传递业务数据：余额、关系值、消息一律由 H5 自己
 * 向后端读取。壳不持有凭证，也不判断计费与权限。
 */

import type { ClientCapabilities } from "@dreamverse/contracts";

export type HostKind = "browser" | "wechat-h5" | "miniapp-webview";

/**
 * 版式形态。
 *
 *   mobile   手机：舞台铺满视口，没有外框
 *   desktop  网页：舞台收成一条手机宽度的列，外圈铺模糊背景
 *
 * 与 HostKind 是两个维度：微信内 H5 一定是 mobile，但普通浏览器可能是任意一种。
 * 判定同时看输入方式和视口——只看 UA 会漏掉平板与桌面版微信，
 * 只看宽度会把窄窗口的桌面浏览器误判成手机。
 */
export type FormFactor = "mobile" | "desktop";

/** 能力不可用时必须给出原因，上层据此决定降级呈现，不静默失败。 */
export interface CapabilityStatus {
  available: boolean;
  /** 不可用原因。available 为 true 时为 undefined。 */
  reason?: string;
}

export interface HostProfile {
  kind: HostKind;
  label: string;
  formFactor: FormFactor;
  /** 客户端能力，随请求上报给后端，后端据此决定可用 Skill。 */
  capabilities: ClientCapabilities;
  /** 需要宿主特权的能力，浏览器里大多不可用。 */
  wechatLogin: CapabilityStatus;
  payment: CapabilityStatus;
  subscription: CapabilityStatus;
  rewardedAd: CapabilityStatus;
}

interface WeixinGlobals {
  __wxjs_environment?: string;
}

function detectHostKind(): HostKind {
  if (typeof window === "undefined") return "browser";

  // 小程序 web-view 会注入 __wxjs_environment === 'miniprogram'。
  // 这是微信官方约定，比 UA 更可靠。
  const wx = window as unknown as WeixinGlobals;
  if (wx.__wxjs_environment === "miniprogram") return "miniapp-webview";
  // 新壳会显式带 host=miniapp。它只影响能力呈现，不参与角色或计费身份解析。
  if (new URL(window.location.href).searchParams.get("host") === "miniapp") {
    return "miniapp-webview";
  }

  // 只有到这里才允许看 UA，且结果不出这个文件。
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("micromessenger")) return "wechat-h5";

  return "browser";
}

function detectMediaCapabilities(): ClientCapabilities {
  const canRecord =
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof window.MediaRecorder === "function";

  const canPlayAudio =
    typeof window !== "undefined" && typeof window.Audio === "function";

  return {
    imageDisplay: true,
    audioPlayback: canPlayAudio,
    musicPlayback: canPlayAudio,
    // 注意：这里只探测「API 存在」，不代表已获授权。
    // 真正的授权状态要等用户手势触发一次 getUserMedia 才知道，
    // 在 iOS 微信内还需要实测，见 apps/web/README.md 的 Spike 清单。
    audioRecording: canRecord,
    subscriptionMessage: false,
  };
}

function detectFormFactor(kind: HostKind): FormFactor {
  if (typeof window === "undefined") return "desktop";
  // 微信内与小程序壳内必然是手机形态，不用再猜。
  if (kind !== "browser") return "mobile";

  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const touch = navigator.maxTouchPoints > 0;
  const narrow = window.innerWidth <= 560;

  // 触摸 + 粗指针 = 手机或平板；窄视口的桌面浏览器也按手机排，
  // 因为那个宽度下网页版的外框反而挤掉了内容。
  return (coarse && touch) || narrow ? "mobile" : "desktop";
}

const HOST_LABEL: Record<HostKind, string> = {
  browser: "浏览器",
  "wechat-h5": "微信内置浏览器",
  "miniapp-webview": "小程序壳",
};

let cached: HostProfile | null = null;

export function getHostProfile(): HostProfile {
  if (cached) return cached;

  const kind = detectHostKind();
  const capabilities = detectMediaCapabilities();

  const unavailable = (reason: string): CapabilityStatus => ({
    available: false,
    reason,
  });

  const profile: HostProfile = {
    kind,
    label: HOST_LABEL[kind],
    formFactor: detectFormFactor(kind),
    capabilities: {
      ...capabilities,
      subscriptionMessage: kind !== "browser",
    },
    wechatLogin:
      kind === "browser"
        ? unavailable("当前不在微信内，改用手机号 + 验证码登录")
        : { available: true },
    payment:
      kind === "miniapp-webview"
        ? { available: true }
        : unavailable(
            kind === "wechat-h5"
              ? "服务号 JSAPI 支付尚未接入，请在小程序中充值"
              : "网页环境无法调起微信支付",
          ),
    subscription:
      kind === "browser"
        ? unavailable("网页环境无法申请订阅消息授权")
        : { available: true },
    // 激励视频只有小程序原生页可用，web-view 里也不行。
    rewardedAd: unavailable(
      kind === "miniapp-webview"
        ? "激励视频只能在小程序原生页播放，需由壳跳转"
        : "当前宿主不支持激励视频广告",
    ),
  };

  cached = profile;
  return profile;
}

/** 测试与 Storybook 用。生产代码不要调用。 */
export function resetHostProfileCache(): void {
  cached = null;
}

/* -------------------------------------------------------------------------- */
/* 登录相关的宿主协议                                                          */
/* -------------------------------------------------------------------------- */

/**
 * 上报给后端的宿主标识。
 *
 * 前端的 `HostKind` 是给人看的，后端的 `AuthSession.hostKind` 是枚举列，
 * 两边命名不同。翻译只发生在这里，业务代码两边都不认另一边的写法。
 *
 * 注意这个值**只影响能力过滤**。决定角色身份与计费的 productKey
 * 由服务端从 Session 反查，客户端无论如何上报都改不了它。
 */
export type ApiHostKind = "web" | "wechat_h5" | "miniapp_shell";

const API_HOST_KIND: Record<HostKind, ApiHostKind> = {
  browser: "web",
  "wechat-h5": "wechat_h5",
  "miniapp-webview": "miniapp_shell",
};

export function apiHostKind(): ApiHostKind {
  return API_HOST_KIND[getHostProfile().kind];
}

/** 新版小程序壳把 `wx.login` 的 code 挂在 web-view 地址上传进来时用的参数名。 */
const MINIAPP_CODE_PARAM = "wxcode";

/**
 * 2026-08-18 切换期间，已发布旧壳只能把接口返回的 `access_token` 放进这些参数之一。
 * 线上正式版实测使用 `h5token`；`zkactoken` 是另一版旧壳使用的名字，二者都要兼容。
 * 兼容接口返回的并不是长期 Token，而是同一个微信一次性 code。这里不定时失效；
 * 至少保留两周，且只有新版壳发布并确认旧接口已无流量后，才能与
 * `/api/wx/login` 兼容控制器一起手动删除。
 */
const LEGACY_MINIAPP_CODE_PARAMS = ["zkactoken", "h5token"] as const;

interface MiniProgramBridge {
  navigateTo?: (options: {
    url: string;
    success?: () => void;
    fail?: () => void;
  }) => void;
}

interface WeixinSdk {
  miniProgram?: MiniProgramBridge;
}

/**
 * 取走小程序壳传进来的登录 code。
 *
 * `<web-view>` 里调不到 `wx.login`，所以 code 只能由壳产生。协议是：
 * 壳先 `wx.login` 拿到 code，再把它拼进 web-view 的地址。详见
 * `docs/MINIAPP_SHELL.md`。壳实现位于相邻仓库 `../miaomiao-wx-webview/`。
 *
 * **取走即从地址栏抹掉。** code 是一次性凭据，留在 URL 里会进入
 * 浏览器历史与 Referer 头——旧项目的 `h5token` 就是这么泄漏的，
 * 而且那还是个 30 天的长期 Token。
 */
export function takeMiniappLoginCode(): string | null {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const currentCode = url.searchParams.get(MINIAPP_CODE_PARAM);
  const legacyCode =
    url.searchParams.get("from") === "wx"
      ? LEGACY_MINIAPP_CODE_PARAMS.map((name) => url.searchParams.get(name)).find(Boolean) ?? null
      : null;
  const code = currentCode ?? legacyCode;
  if (!code) return null;

  url.searchParams.delete(MINIAPP_CODE_PARAM);
  for (const name of LEGACY_MINIAPP_CODE_PARAMS) url.searchParams.delete(name);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  return code;
}

/**
 * 请壳重新发一次 code。
 *
 * 主路径仍是「壳在打开 web-view 前准备 code」。只有用户真的需要重新登录
 * 或调起支付时，桥接层才懒加载微信 JSSDK，不给普通浏览器增加外部脚本请求。
 */
export async function requestMiniappLogin(redirect: string): Promise<boolean> {
  return navigateMiniappPage(
    `/pages/login/login?redirect=${encodeURIComponent(safeH5Redirect(redirect))}`,
  );
}

/* -------------------------------------------------------------------------- */
/* 支付相关的宿主协议                                                          */
/* -------------------------------------------------------------------------- */

export interface VirtualPaymentTicket { kind: "wechat_virtual"; ticket: string }
export type MiniappPaymentPayload = VirtualPaymentTicket | {
  kind?: "wechat";
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: "RSA";
  paySign: string;
};
export type MiniappPaymentRequest = MiniappPaymentPayload & {
  orderId: string;
  actionId: string;
  redirect: string;
};

/**
 * 跳到壳的原生支付页。参数全部来自后端微信预下单响应；H5 不重算签名，
 * 壳也不决定金额。支付页结束后会重新打开 redirect，并带回 orderId/actionId。
 */
export async function requestMiniappPayment(input: MiniappPaymentRequest): Promise<boolean> {
  if (!/^[0-9a-f-]{36}$/i.test(input.orderId)) return false;
  if (!/^[A-Za-z0-9_-]{8,64}$/.test(input.actionId)) return false;

  const query = new URLSearchParams({
    orderId: input.orderId,
    actionId: input.actionId,
    redirect: safeH5Redirect(input.redirect),
  });
  if (input.kind === "wechat_virtual") {
    if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(input.ticket) || input.ticket.length > 1024) return false;
    query.set("kind", input.kind);
    query.set("ticket", input.ticket);
  } else {
    for (const field of ["timeStamp", "nonceStr", "package", "signType", "paySign"] as const) query.set(field, input[field]);
  }
  return navigateMiniappPage(`/pages/pay/pay?${query.toString()}`);
}

let sdkPromise: Promise<WeixinSdk | null> | null = null;

async function navigateMiniappPage(url: string): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (getHostProfile().kind !== "miniapp-webview") return false;
  const sdk = await ensureWeixinSdk();
  const navigateTo = sdk?.miniProgram?.navigateTo;
  if (typeof navigateTo !== "function") return false;

  try {
    navigateTo({ url });
    return true;
  } catch {
    return false;
  }
}

/** 小程序 web-view 桥接由微信 JSSDK 提供；只在真正要跳原生页时懒加载。 */
function ensureWeixinSdk(): Promise<WeixinSdk | null> {
  const current = (window as unknown as { wx?: WeixinSdk }).wx;
  if (typeof current?.miniProgram?.navigateTo === "function") return Promise.resolve(current);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-dreamverse-wx-sdk]');
    const script = existing ?? document.createElement("script");
    const finish = () => {
      const sdk = (window as unknown as { wx?: WeixinSdk }).wx;
      resolve(typeof sdk?.miniProgram?.navigateTo === "function" ? sdk : null);
    };
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => resolve(null), { once: true });
    if (!existing) {
      script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
      script.async = true;
      script.dataset.dreamverseWxSdk = "true";
      document.head.appendChild(script);
    }
    window.setTimeout(finish, 5000);
  });
  return sdkPromise;
}

function safeH5Redirect(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  return value.slice(0, 512);
}
